import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../shared/button";
import { AuthButtons } from "./AuthButtons";
import { useAuthState } from "@/hooks/useAuthState";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { ROUTES } from "@/config/routes";

export const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthState();
  const [hasSavedAssessment, setHasSavedAssessment] = useState(false);
  const isReadinessActive = location.pathname.startsWith('/readiness');
  const isOpportunityActive = location.pathname.startsWith('/assessment');
  const isBenchmarkingActive = location.pathname.startsWith('/benchmarking');

  useEffect(() => {
    const checkSavedAssessment = async () => {
      if (user) {
        console.log('Checking for saved assessments for user:', user.id);
        const { data: assessments, error } = await supabase
          .from('assessments')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (!error && assessments && assessments.length > 0) {
          console.log('Found saved assessment:', assessments[0].id);
          setHasSavedAssessment(true);
        } else {
          console.log('No saved assessments found');
          setHasSavedAssessment(false);
        }
      }
    };

    checkSavedAssessment();
  }, [user]);

  const handleOpportunityClick = (e: React.MouseEvent) => {
    e.preventDefault();
    console.log('Opportunity link clicked, hasSavedAssessment:', hasSavedAssessment);
    
    if (!user) {
      console.log('No user logged in, navigating to wizard');
      navigate('/assessment/wizard');
      return;
    }

    if (hasSavedAssessment) {
      console.log('User has saved assessment, navigating to results');
      navigate('/assessment');
    } else {
      console.log('No saved assessment found, navigating to wizard');
      navigate('/assessment/wizard');
    }
  };

  return (
    <>
      <header className="bg-white w-full overflow-hidden py-[21px] max-md:max-w-full font-inter">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32">
          <div className="flex w-full items-center justify-between gap-4">
            <Link to="/" className="flex items-center">
              <img
                src="/lovable-uploads/0dcf3837-946c-450b-b2f4-874d397550a7.png"
                alt="Company Logo"
                className="aspect-[10] object-contain w-[309px] max-w-[200px] sm:max-w-[250px] md:max-w-[309px] shrink-0"
              />
            </Link>
            <nav className="flex items-center">
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6 text-base font-normal leading-normal">
                <Button
                  variant="ghost"
                  className="text-[#CA0079] gap-[11px] whitespace-nowrap px-4 sm:px-[31px] py-[10px] sm:py-[13px] rounded-[10px] hover:bg-[#CA0079] hover:text-white transition-colors text-sm sm:text-base"
                >
                  Support
                </Button>
                <AuthButtons />
              </div>
            </nav>
          </div>
        </div>
      </header>

      <nav className="w-full text-[rgba(33,33,33,1)] mt-[15px] pb-5 max-md:mt-10 font-inter">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32">
          <h1 className="text-2xl font-medium max-md:max-w-full">
            AI-Powered Permitting Toolkit
          </h1>
          <div className="flex w-full items-center gap-[40px_42px] text-base font-normal leading-normal flex-wrap mt-[21px] max-md:max-w-full max-md:flex-col max-md:items-start max-md:gap-4">
            <a
              href="#"
              onClick={handleOpportunityClick}
              className={`relative self-stretch gap-2.5 my-auto py-4 max-md:w-full max-md:text-left transition-colors ${
                isOpportunityActive
                  ? 'text-[#CA0079] font-medium after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-[#CA0079]'
                  : 'text-[#212121] hover:text-[#CA0079] after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-[#CA0079] after:scale-x-0 after:origin-left after:transition-transform after:duration-300 hover:after:scale-x-100'
              }`}
            >
              Opportunity Assessment
            </a>
            <Link
              to="/readiness/assessment"
              className={`relative self-stretch gap-2.5 my-auto py-4 max-md:w-full max-md:text-left transition-colors ${
                isReadinessActive
                  ? 'text-[#CA0079] font-medium after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-[#CA0079]'
                  : 'text-[#212121] hover:text-[#CA0079] after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-[#CA0079] after:scale-x-0 after:origin-left after:transition-transform after:duration-300 hover:after:scale-x-100'
              }`}
            >
              Readiness Assessment
            </Link>
            <Link
              to={ROUTES.PERMIT_BENCHMARKING.ROOT}
              className={`relative self-stretch gap-2.5 my-auto py-4 max-md:w-full max-md:text-left transition-colors ${
                isBenchmarkingActive
                  ? 'text-[#CA0079] font-medium after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-[#CA0079]'
                  : 'text-[#212121] hover:text-[#CA0079] after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[3px] after:bg-[#CA0079] after:scale-x-0 after:origin-left after:transition-transform after:duration-300 hover:after:scale-x-100'
              }`}
            >
              Permit Benchmarking
            </Link>
            <button
              disabled
              className="relative self-stretch gap-2.5 my-auto py-4 max-md:w-full max-md:text-left transition-colors text-gray-400 cursor-not-allowed"
            >
              ROI Calculator
            </button>
            <button
              disabled
              className="relative self-stretch gap-2.5 my-auto py-4 max-md:w-full max-md:text-left transition-colors text-gray-400 cursor-not-allowed"
            >
              City Profile
            </button>
          </div>
        </div>
      </nav>
    </>
  );
};